import React, { useEffect, useRef, useState } from 'react'
import { getPeakDbFS } from '@/audio/meters'

/**
 * Thin horizontal peak meter driven by the shared `meters` module.
 * Reads `getPeakDbFS(meterId)` on every animation frame; the meters
 * module owns the AnalyserNode + scratch buffer so each meter
 * instance just reads + renders.
 *
 * Visual language:
 *   - Width spans [-60 dBFS .. 0 dBFS] mapped linearly to 0..100%.
 *     Anything below -60 dB shows as a stub; clipping (> 0 dB) caps
 *     the bar at 100%.
 *   - Color ramp: green < -12 dB, yellow -12..-6 dB, red > -6 dB.
 *     Matches the common DAW convention that red means "you're
 *     about to clip."
 *   - Decays toward silent at ~60 dB/sec so peaks stay readable
 *     without feeling sluggish.
 *
 * Gated by `enabled` — pass `false` to pause the RAF loop when the
 * containing panel is closed or Monitor is off, so we don't poll
 * the meter for a signal that isn't flowing.
 */
interface InputMeterProps {
  meterId: string
  enabled: boolean
  /** Optional className forwarded to the outer wrapper so callers
   *  can set width / margins without a wrapper div. */
  className?: string
}

const FLOOR_DB = -60
const DECAY_DB_PER_SEC = 60

export const InputMeter: React.FC<InputMeterProps> = ({
  meterId,
  enabled,
  className = '',
}) => {
  const [displayDb, setDisplayDb] = useState(FLOOR_DB)
  const rafRef = useRef<number | null>(null)
  // Initialised lazily inside the effect — calling `performance.now`
  // during render would violate the React-Compiler purity rule; the
  // timestamp only matters for the first animation frame anyway.
  const lastFrameMsRef = useRef<number>(0)
  const smoothedRef = useRef(FLOOR_DB)

  useEffect(() => {
    if (!enabled) {
      smoothedRef.current = FLOOR_DB
      setDisplayDb(FLOOR_DB)
      return
    }
    const tick = () => {
      const now = performance.now()
      const dt = Math.max(0, now - lastFrameMsRef.current) / 1000
      lastFrameMsRef.current = now
      const raw = getPeakDbFS(meterId)
      // Peak-hold + linear decay: immediate rise (loudness should
      // snap up), gradual fall. Matches the response most DAW
      // meters use.
      const smoothed = smoothedRef.current
      const decayed = smoothed - DECAY_DB_PER_SEC * dt
      const next = Math.max(raw, decayed, FLOOR_DB)
      smoothedRef.current = next
      setDisplayDb(next)
      rafRef.current = requestAnimationFrame(tick)
    }
    lastFrameMsRef.current = performance.now()
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [meterId, enabled])

  // Map [-60, 0] dBFS → [0, 100%] via a mild square-root curve so
  // typical speaking-voice peaks (−20 to −10 dBFS) visibly fill most
  // of the bar instead of hugging the left edge. Pure linear
  // `(db - floor) / -floor` mapped voice to ~33-60% width, which
  // the user flagged as "only fills 1/8 to 1/4" at red. The 0.6
  // exponent is a judgment call — it visually reads as "calibrated
  // like a DAW meter" without hitting full red on quiet speech.
  const normalized = Math.max(
    0,
    Math.min(1, (displayDb - FLOOR_DB) / -FLOOR_DB),
  )
  const pct = Math.pow(normalized, 0.6) * 100
  const color =
    displayDb > -6
      ? 'bg-destructive'
      : displayDb > -12
        ? 'bg-[color:var(--second)]'
        : 'bg-accent'

  return (
    <div
      className={`relative h-2 rounded bg-surface-sunken border border-brd-subtle overflow-hidden ${className}`}
      aria-label="Input level"
      title={`${displayDb > FLOOR_DB ? displayDb.toFixed(1) : '—'} dBFS`}
    >
      <div
        className={`h-full transition-[width] duration-75 ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}
